import { LitElement, css, html } from 'lit';
import { customElement } from 'lit/decorators.js';
import '../components/copy-email-button';
import '../components/ui/ui-button';
import { AGENCY_SCOPE } from '../content/agency';
import { navigate } from '../router';
import { boxSizingStyles, fullBleedStyles } from '../styles/lit-shared';

const CALENDAR_URL = 'https://calendar.app.google/3TMX3h7SGT1wkdp87';
const JSON_LD_ID = 'jsonld-agencias';
const BLUNTICLE_URL = 'https://blunticle.alejandrosn.com';

const PROCESS = [
  {
    label: 'Brief',
    title: 'Alcance cerrado antes de construir',
    description:
      'Definimos qué entra, qué no y cómo se entrega. Tú lo usas con tu cliente; yo trabajo contra ese documento, no contra un chat eterno.',
  },
  {
    label: 'Relación',
    title: 'Tú hablas con el cliente',
    description:
      'La cuenta es tuya. Yo no sustituyo al PM ni me cuelo en la relación comercial. Si hace falta una llamada técnica, me presentas tú.',
  },
  {
    label: 'Marca blanca',
    title: 'Sin contacto sin autorización',
    description:
      'No contacto con tu cliente por mi cuenta. El encargo se entrega para que lo firmes tú. Marca blanca como proceso, no como eslogan.',
  },
  {
    label: 'Entrega',
    title: 'Código y contexto, listos para traspasar',
    description:
      'Documentación, decisiones y lo que tu equipo necesita para mantenerlo. Si seguimos, seguimos; si os lo quedáis, no os dejo atados.',
  },
] as const;

const NOT_THIS = [
  {
    title: 'No sustituyo al PM',
    description:
      'Tú priorizas, tú presentas, tú cierras. Yo ejecuto el desarrollo, las integraciones o la automatización del encargo.',
  },
  {
    title: 'No soy una fábrica WordPress',
    description:
      'Si buscas maquetación a volumen en un CMS, hay estudios mejores para eso. Aquí: APIs, automatización, paneles y webs a medida.',
  },
  {
    title: 'No invento clientes de agencia',
    description:
      'No hay logos que no pueda enseñar. La prueba de oficio son productos propios en producción, no un carrusel de marcas prestadas.',
  },
] as const;

const PROOF = [
  {
    name: 'SubbySwap',
    description: 'Matchmaking y verificación de intercambios entre newsletters de Substack.',
    path: '/subbyswap',
  },
  {
    name: 'Blunticle',
    description: 'Herramienta de contenido con un equipo de agentes de IA, en producción.',
    href: BLUNTICLE_URL,
  },
  {
    name: 'Traviapp',
    description: 'Link-in-bio con perfil, secciones, suscriptores y analítica.',
    path: '/traviapp',
  },
  {
    name: 'NFC Pop',
    description: 'Soporte físico NFC/QR con URL dinámica para comercios.',
    path: '/tag-nfc',
  },
] as const;

const AGENCIES_JSON_LD = {
  '@context': 'https://schema.org',
  '@type': 'ProfessionalService',
  '@id': 'https://alejandrosn.com/agencias#service',
  name: 'AlejandroSN — Partner técnico para agencias',
  url: 'https://alejandrosn.com/agencias',
  description:
    'Desarrollador freelance para agencias: integraciones, automatización y webs a medida. Tú mantienes al cliente; yo ejecuto el encargo.',
  provider: {
    '@type': 'Person',
    '@id': 'https://alejandrosn.com/#person',
    name: 'Alejandro Serrano Notivoli',
    url: 'https://alejandrosn.com/',
  },
  areaServed: 'ES',
  serviceType: [
    'Partner técnico para agencias',
    'Desarrollo web marca blanca',
    'Integraciones API',
    'Automatización',
    'Despliegues Cloudflare',
  ],
  audience: {
    '@type': 'Audience',
    audienceType: 'Agencias digitales',
  },
};

@customElement('agencies-page')
export class AgenciesPage extends LitElement {
  private _revealObserver: IntersectionObserver | null = null;

  private goTo(path: string) {
    void navigate(path);
  }

  connectedCallback() {
    super.connectedCallback();
    this._injectJsonLd();
  }

  firstUpdated() {
    this._setupReveal();
  }

  disconnectedCallback() {
    this._revealObserver?.disconnect();
    this._revealObserver = null;
    document.getElementById(JSON_LD_ID)?.remove();
    super.disconnectedCallback();
  }

  private _injectJsonLd() {
    if (document.getElementById(JSON_LD_ID)) return;
    const el = document.createElement('script');
    el.type = 'application/ld+json';
    el.id = JSON_LD_ID;
    el.textContent = JSON.stringify(AGENCIES_JSON_LD);
    document.head.appendChild(el);
  }

  private _setupReveal() {
    const root = this.shadowRoot;
    if (!root) return;

    const els = root.querySelectorAll('.reveal');
    if (!els.length) return;

    if (typeof IntersectionObserver === 'undefined') {
      els.forEach((el) => el.classList.add('is-visible'));
      return;
    }

    this._revealObserver = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            this._revealObserver?.unobserve(entry.target);
          }
        }
      },
      { threshold: 0.12, rootMargin: '0px 0px -8% 0px' },
    );

    els.forEach((el) => this._revealObserver!.observe(el));
  }

  private renderActions() {
    return html`
      <div class="actions">
        <ui-button href=${CALENDAR_URL} variant="primary">Reservar una llamada</ui-button>
        <copy-email-button></copy-email-button>
      </div>
    `;
  }

  static styles = [
    boxSizingStyles,
    fullBleedStyles,
    css`
      :host {
        display: block;
        width: 100%;
      }

      .hero {
        margin-top: calc(-1 * var(--space-xl));
        margin-bottom: var(--space-md);
        padding: var(--space-2xl) var(--space-lg);
        overflow: hidden;
        background:
          radial-gradient(ellipse 70% 60% at 15% 40%, var(--glow-violet), transparent 55%),
          radial-gradient(ellipse 50% 50% at 85% 30%, var(--glow-cyan), transparent 50%),
          radial-gradient(ellipse 40% 100% at 0% 50%, var(--glow-violet), transparent 60%),
          radial-gradient(ellipse 40% 100% at 100% 50%, var(--glow-cyan), transparent 60%),
          var(--color-bg);
      }

      .hero-orb {
        position: absolute;
        border-radius: 50%;
        filter: blur(60px);
        pointer-events: none;
        animation: orb-drift 16s var(--ease-out) infinite alternate;
      }

      .hero-orb--1 {
        top: 10%;
        right: 5%;
        width: min(320px, 45vw);
        height: min(320px, 45vw);
        background: var(--glow-cyan);
      }

      .hero-orb--2 {
        bottom: -10%;
        left: 20%;
        width: min(260px, 40vw);
        height: min(260px, 40vw);
        background: var(--glow-fuchsia);
        animation-delay: -5s;
      }

      @keyframes orb-drift {
        from {
          transform: translate(0, 0) scale(1);
        }
        to {
          transform: translate(18px, -14px) scale(1.06);
        }
      }

      .hero-inner {
        position: relative;
        z-index: 1;
        width: 100%;
        max-width: var(--max-width);
        margin: 0 auto;
      }

      .section-label {
        display: block;
        margin-bottom: var(--space-md);
        font-family: var(--font-mono);
        font-size: 0.6875rem;
        font-weight: 600;
        text-transform: uppercase;
        letter-spacing: 0.12em;
        color: var(--color-accent);
      }

      .hero-title {
        font-size: clamp(2rem, 5vw, 3rem);
        font-weight: 700;
        letter-spacing: -0.035em;
        line-height: 1.1;
        margin: 0 0 var(--space-sm);
        color: var(--color-text);
      }

      .hero-title .title-grad {
        background: var(--gradient-text);
        -webkit-background-clip: text;
        background-clip: text;
        -webkit-text-fill-color: transparent;
      }

      .hero-tagline {
        font-family: var(--font-mono);
        font-size: 0.8125rem;
        font-weight: 500;
        letter-spacing: 0.04em;
        color: var(--color-primary-light);
        margin: 0 0 var(--space-md);
      }

      .hero-lead {
        font-size: 1.0625rem;
        color: var(--color-text-muted);
        line-height: 1.65;
        margin: 0 0 var(--space-xl);
        max-width: 54ch;
      }

      .section {
        padding: var(--space-2xl) var(--space-lg);
        max-width: var(--max-width);
        margin-inline: auto;
      }

      .section-title {
        font-size: clamp(1.75rem, 4vw, 2.5rem);
        font-weight: 700;
        letter-spacing: -0.03em;
        line-height: 1.15;
        margin: 0 0 var(--space-sm);
        color: var(--color-text);
      }

      .section-title .title-grad {
        background: var(--gradient-text);
        -webkit-background-clip: text;
        background-clip: text;
        -webkit-text-fill-color: transparent;
      }

      .section-lead {
        color: var(--color-text-muted);
        font-size: 1.0625rem;
        line-height: 1.65;
        margin: 0 0 var(--space-xl);
        max-width: 58ch;
      }

      .reveal {
        opacity: 0;
        transform: translateY(18px);
        transition:
          opacity 700ms var(--ease-out),
          transform 700ms var(--ease-out);
      }

      .reveal.is-visible {
        opacity: 1;
        transform: translateY(0);
      }

      .reveal-delay-1 {
        transition-delay: 80ms;
      }
      .reveal-delay-2 {
        transition-delay: 160ms;
      }
      .reveal-delay-3 {
        transition-delay: 240ms;
      }
      .reveal-delay-4 {
        transition-delay: 320ms;
      }

      .band {
        padding: var(--space-2xl) var(--space-lg);
        overflow: hidden;
      }

      .band-inner {
        position: relative;
        z-index: 1;
        width: 100%;
        max-width: var(--max-width);
        margin: 0 auto;
      }

      .band--story {
        background:
          radial-gradient(ellipse 70% 80% at 0% 40%, var(--glow-violet), transparent 55%),
          radial-gradient(ellipse 45% 55% at 100% 70%, var(--glow-cyan), transparent 50%),
          radial-gradient(ellipse 40% 100% at 0% 50%, var(--glow-violet), transparent 60%),
          radial-gradient(ellipse 40% 100% at 100% 50%, var(--glow-cyan), transparent 60%),
          var(--color-bg);
      }

      .band--story .band-inner {
        padding-left: var(--space-xl);
        border-left: 2px solid transparent;
        border-image: linear-gradient(
            to bottom,
            var(--color-accent),
            var(--color-primary-light),
            transparent
          )
          1;
      }

      .story p {
        margin: 0 0 var(--space-md);
        color: var(--color-text-muted);
        font-size: 1.0625rem;
        line-height: 1.65;
        max-width: 62ch;
      }

      .story p:last-child {
        margin-bottom: 0;
      }

      .open-grid {
        display: grid;
        gap: 0;
        grid-template-columns: 1fr;
        border-top: 1px solid var(--color-border);
      }

      @media (min-width: 860px) {
        .open-grid {
          grid-template-columns: repeat(3, 1fr);
        }

        .open-grid--two {
          grid-template-columns: repeat(2, 1fr);
        }
      }

      .open-item {
        padding: var(--space-lg) var(--space-md) var(--space-lg) 0;
        border-bottom: 1px solid var(--color-border);
      }

      @media (min-width: 860px) {
        .open-item {
          padding-right: var(--space-lg);
          border-right: 1px solid var(--color-border);
          padding-left: var(--space-lg);
        }

        .open-grid:not(.open-grid--two) .open-item:nth-child(3n) {
          padding-right: 0;
          border-right: none;
        }

        .open-grid--two .open-item:nth-child(2n) {
          padding-right: 0;
          border-right: none;
        }
      }

      .open-item .item-label {
        display: block;
        font-family: var(--font-mono);
        font-size: 0.6875rem;
        font-weight: 600;
        text-transform: uppercase;
        letter-spacing: 0.1em;
        color: var(--color-accent);
        margin-bottom: var(--space-xs);
      }

      .open-item h3 {
        margin: 0 0 var(--space-xs);
        font-size: 1.125rem;
        font-weight: 600;
        letter-spacing: -0.02em;
        color: var(--color-text);
      }

      .open-item p {
        margin: 0;
        color: var(--color-text-muted);
        font-size: 0.9375rem;
        line-height: 1.6;
      }

      .open-item ui-button {
        margin-top: var(--space-md);
      }

      .agency-scope {
        display: grid;
        grid-template-columns: 1fr;
        gap: 0.55rem var(--space-xl);
        margin: 0;
        padding: 0;
        list-style: none;
      }

      @media (min-width: 700px) {
        .agency-scope {
          grid-template-columns: repeat(2, 1fr);
        }
      }

      .agency-scope li {
        position: relative;
        padding-left: 1rem;
        color: var(--color-text-muted);
        font-size: 0.975rem;
        line-height: 1.55;
      }

      .agency-scope li::before {
        content: '';
        position: absolute;
        left: 0;
        top: 0.55em;
        width: 0.35rem;
        height: 0.35rem;
        border-radius: 50%;
        background: var(--color-accent);
        box-shadow: 0 0 8px rgb(34 211 238 / 40%);
      }

      .band--cta {
        margin-top: var(--space-md);
        margin-bottom: var(--space-xl);
        text-align: center;
        background:
          radial-gradient(ellipse 55% 65% at 35% 40%, var(--glow-violet), transparent 55%),
          radial-gradient(ellipse 45% 55% at 80% 60%, var(--glow-cyan), transparent 50%),
          radial-gradient(ellipse 40% 100% at 0% 50%, var(--glow-violet), transparent 60%),
          radial-gradient(ellipse 40% 100% at 100% 50%, var(--glow-cyan), transparent 60%),
          var(--color-bg);
      }

      .band--cta .section-title {
        margin-bottom: var(--space-md);
      }

      .band-lead {
        color: var(--color-text-muted);
        font-size: 1.0625rem;
        line-height: 1.65;
        margin: 0 auto var(--space-xl);
        max-width: 52ch;
      }

      .band-orb {
        position: absolute;
        border-radius: 50%;
        filter: blur(70px);
        pointer-events: none;
        animation: orb-drift 18s var(--ease-out) infinite alternate;
      }

      .band-orb--1 {
        top: -15%;
        right: 8%;
        width: min(280px, 40vw);
        height: min(280px, 40vw);
        background: var(--glow-fuchsia);
      }

      .actions {
        display: flex;
        flex-wrap: wrap;
        gap: var(--space-md);
      }

      .hero .actions {
        justify-content: flex-start;
      }

      .band--cta .actions {
        justify-content: center;
      }

      @media (max-width: 639px) {
        .hero {
          margin-top: calc(-1 * var(--space-lg));
          padding: var(--space-2xl) var(--space-md);
        }

        .band,
        .section {
          padding: var(--space-2xl) var(--space-md);
        }

        .band--story .band-inner {
          padding-left: var(--space-lg);
        }
      }

      @media (prefers-reduced-motion: reduce) {
        .hero-orb,
        .band-orb {
          animation: none;
        }

        .reveal {
          opacity: 1;
          transform: none;
          transition: none;
        }
      }
    `,
  ];

  render() {
    return html`
      <section class="hero full-bleed" aria-label="Partner técnico para agencias">
        <span class="hero-orb hero-orb--1" aria-hidden="true"></span>
        <span class="hero-orb hero-orb--2" aria-hidden="true"></span>

        <div class="hero-inner">
          <span class="section-label">Agencias</span>
          <h1 class="hero-title">
            Partner técnico para <span class="title-grad">agencias</span>
          </h1>
          <p class="hero-tagline">Freelance · integraciones, automatización y Cloudflare</p>
          <p class="hero-lead">
            Tú vendes y conservas la relación. Yo construyo el encargo: desarrollo,
            integraciones o automatización, con alcance cerrado y sin meterme con tu cliente.
          </p>
          ${this.renderActions()}
        </div>
      </section>

      <section class="band band--story full-bleed" aria-label="Para quién es esto">
        <div class="band-inner story reveal">
          <span class="section-label">Encargo</span>
          <h2 class="section-title">
            Capacidad extra, <span class="title-grad">sin perder al cliente</span>
          </h2>
          <p>
            Si vuestra agencia tiene un proyecto que pide desarrollo y no queréis asumirlo
            internamente, puedo encargarme de la ejecución técnica. Hablo con vosotros, no con
            quien os paga a vosotros.
          </p>
          <p>
            No busco sustituir a la agencia. Busco ser la persona a la que llamáis cuando el
            brief ya está vendido y falta alguien que lo construya.
          </p>
        </div>
      </section>

      <section class="section" aria-label="Qué puedo ejecutar">
        <span class="section-label reveal">Alcance</span>
        <h2 class="section-title reveal reveal-delay-1">
          Lo que puedo <span class="title-grad">ejecutar</span>
        </h2>
        <p class="section-lead reveal reveal-delay-2">
          Desarrollo freelance para agencias: APIs, automatización, paneles y webs a medida.
          Cloudflare cuando el despliegue tiene que ser ligero.
        </p>
        <ul class="agency-scope reveal reveal-delay-3" aria-label="Servicios para agencias">
          ${AGENCY_SCOPE.map((item) => html`<li>${item}</li>`)}
        </ul>
      </section>

      <section class="section" aria-label="Cómo colaboramos">
        <span class="section-label reveal">Proceso</span>
        <h2 class="section-title reveal reveal-delay-1">Cómo colaboramos</h2>
        <p class="section-lead reveal reveal-delay-2">
          Marca blanca como forma de trabajar: tú pones la cara, yo pongo el código.
        </p>
        <div class="open-grid open-grid--two">
          ${PROCESS.map(
            (item, i) => html`
              <article class="open-item reveal ${i > 0 ? `reveal-delay-${Math.min(i, 4)}` : ''}">
                <span class="item-label">${item.label}</span>
                <h3>${item.title}</h3>
                <p>${item.description}</p>
              </article>
            `,
          )}
        </div>
      </section>

      <section class="section" aria-label="Qué no soy">
        <span class="section-label reveal">Límites</span>
        <h2 class="section-title reveal reveal-delay-1">Qué no soy</h2>
        <div class="open-grid">
          ${NOT_THIS.map(
            (item, i) => html`
              <article class="open-item reveal ${i > 0 ? `reveal-delay-${i}` : ''}">
                <h3>${item.title}</h3>
                <p>${item.description}</p>
              </article>
            `,
          )}
        </div>
      </section>

      <section class="section" aria-label="Prueba de oficio">
        <span class="section-label reveal">Oficio</span>
        <h2 class="section-title reveal reveal-delay-1">
          Productos propios, no <span class="title-grad">casos inventados</span>
        </h2>
        <p class="section-lead reveal reveal-delay-2">
          Lo que puedo enseñar está en producción. Si encaja con lo que os falta, lo vemos en
          una llamada.
        </p>
        <div class="open-grid open-grid--two">
          ${PROOF.map(
            (item, i) => html`
              <article class="open-item reveal ${i > 0 ? `reveal-delay-${Math.min(i, 4)}` : ''}">
                <h3>${item.name}</h3>
                <p>${item.description}</p>
                ${'href' in item
                  ? html`<ui-button href=${item.href} variant="secondary">Ver proyecto</ui-button>`
                  : html`<ui-button variant="secondary" @ui-click=${() => this.goTo(item.path)}
                      >Ver proyecto</ui-button
                    >`}
              </article>
            `,
          )}
        </div>
      </section>

      <section class="band band--cta full-bleed" aria-label="Hablemos del encargo">
        <span class="band-orb band-orb--1" aria-hidden="true"></span>
        <div class="band-inner reveal">
          <span class="section-label">Encargo</span>
          <h2 class="section-title">
            Si hay un proyecto, <span class="title-grad">hablemos</span>
          </h2>
          <p class="band-lead">
            Reserva una llamada. Si preferís escribir, el email está aquí. Sin contacto con vuestro
            cliente sin autorización.
          </p>
          ${this.renderActions()}
        </div>
      </section>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'agencies-page': AgenciesPage;
  }
}
